import {
  AlignLeft,
  Calendar,
  Camera,
  CircleDot,
  Hash,
  Heading,
  Images,
  Info,
  ListChecks,
  SquareCheck,
  Table,
  Type,
} from 'lucide-react';
import type React from 'react';

export const CHECKLIST_QUESTION_TYPES = [
  'yes_no',
  'short_text',
  'text',
  'number',
  'date',
  'single_choice',
  'multiple_choice',
  'photo',
  'photos',
  'table',
  'info',
  'section',
] as const;

export type ChecklistQuestionType = (typeof CHECKLIST_QUESTION_TYPES)[number];

/** Display-only rows: no Field answer, never required. */
export const CHECKLIST_DISPLAY_ONLY_TYPES = ['info', 'section'] as const;

export type ChecklistDisplayOnlyType = (typeof CHECKLIST_DISPLAY_ONLY_TYPES)[number];

export function isChecklistDisplayOnlyType(type: string): type is ChecklistDisplayOnlyType {
  return (CHECKLIST_DISPLAY_ONLY_TYPES as readonly string[]).includes(type);
}

export const CHECKLIST_SHORT_TEXT_MAX_LENGTH = 200;

export const CHECKLIST_QUESTION_TYPE_LABELS: Record<ChecklistQuestionType, string> = {
  yes_no: 'Ja / Nein',
  short_text: 'Kurztext',
  text: 'Langtext',
  number: 'Zahl',
  date: 'Datum',
  single_choice: 'Einfachauswahl',
  multiple_choice: 'Mehrfachauswahl',
  photo: 'Ein Bild',
  photos: 'Mehrere Bilder',
  table: 'Tabelle',
  info: 'Hinweis',
  section: 'Abschnitt',
};

export const CHECKLIST_QUESTION_TYPE_ICONS: Record<
  ChecklistQuestionType,
  React.ComponentType<{ className?: string }>
> = {
  yes_no: SquareCheck,
  short_text: Type,
  text: AlignLeft,
  number: Hash,
  date: Calendar,
  single_choice: CircleDot,
  multiple_choice: ListChecks,
  photo: Camera,
  photos: Images,
  table: Table,
  info: Info,
  section: Heading,
};

export function checklistQuestionTypeIcon(type: ChecklistQuestionType) {
  const Icon = CHECKLIST_QUESTION_TYPE_ICONS[type] ?? ListChecks;
  return Icon;
}

export function createEmptyChecklistQuestion(type: ChecklistQuestionType = 'text') {
  return {
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    text: '',
    question_type: type,
    required: false,
    config: defaultChecklistQuestionConfig(type),
  };
}

/** Starting config when a question is created or switched to another type. */
export function defaultChecklistQuestionConfig(type: ChecklistQuestionType): Record<string, unknown> {
  if (type === 'single_choice' || type === 'multiple_choice') return { options: ['', ''] };
  if (type === 'table') {
    return {
      columns: [
        { key: `c_${Math.random().toString(36).slice(2, 10)}`, label: 'Datum', type: 'date' },
        { key: `c_${Math.random().toString(36).slice(2, 10)}`, label: 'Beschreibung', type: 'text' },
      ],
    };
  }
  return {};
}

/** Types shown in the answer-type picker (Abschnitt/Hinweis have dedicated add buttons). */
export const CHECKLIST_ANSWER_QUESTION_TYPES = CHECKLIST_QUESTION_TYPES.filter(
  (t) => t !== 'section' && t !== 'info',
);
